// The response envelope `{ success, data, message, error? }` (doc 07) and the platform errors every operation can
// return. The adapter, the client and the OpenAPI emitter all build envelopes from here; none defines its own.
import { z } from 'zod';
import { defineError, type ErrorDef, type ErrorOf } from '../define/errors.ts';

export const ValidationFailed = defineError({
  code: 'validation_failed', httpStatus: 422, message: 'errors.validation_failed', retryable: false,
  details: z.object({ issues: z.array(z.object({ path: z.string(), message: z.string() })) }),
});
export const Unauthenticated = defineError({
  code: 'unauthenticated', httpStatus: 401, message: 'errors.unauthenticated', retryable: false, details: z.object({}),
});
export const Forbidden = defineError({
  code: 'forbidden', httpStatus: 403, message: 'errors.forbidden', retryable: false,
  details: z.object({ permission: z.string() }),
});
export const IdempotencyConflict = defineError({
  code: 'idempotency_conflict', httpStatus: 409, message: 'errors.idempotency_conflict', retryable: false,
  details: z.object({ commandId: z.string() }),
});
export const NeedsInternet = defineError({
  code: 'needs_internet', httpStatus: 503, message: 'errors.needs_internet', retryable: true,
  details: z.object({ operation: z.string() }),
});
/**
 * A location node whose database disk is below its floor of free space refuses new writes (chaos row C12) rather than
 * fail mid-write on a full disk. Retryable: the device or person sends it again once space is freed.
 */
export const StorageFull = defineError({
  code: 'storage_full', httpStatus: 503, message: 'errors.storage_full', retryable: true,
  details: z.object({ freeBytes: z.int().nonnegative(), floorBytes: z.int().nonnegative() }),
});
/**
 * One principal (or, before one is known, one client address) sent more operation calls in a minute than the server
 * takes (SEC-118f). Retryable after `retryAfterSeconds`, which the answer's Retry-After header also carries.
 */
export const RateLimited = defineError({
  code: 'rate_limited', httpStatus: 429, message: 'errors.rate_limited', retryable: true,
  details: z.object({ retryAfterSeconds: z.int().positive() }),
});
/**
 * HUB-06 (hub acceptance test 6): a fact a device decides (a command whose `origin` is `device`) reaches a server only as
 * an event through sync push, never as a REST call from the device. A device principal that calls one over
 * `/v1/commands/*` is refused with this, so a register can't record a sale outside its own outbox and watermark. People
 * (back office, staff) may still call those commands over REST. Not retryable: the device records the fact and syncs it.
 */
export const UseEvents = defineError({
  code: 'use_events', httpStatus: 409, message: 'errors.use_events', retryable: false,
  details: z.object({ operation: z.string() }),
});
export const InternalError = defineError({
  code: 'internal_error', httpStatus: 500, message: 'errors.internal_error', retryable: true, details: z.object({}),
});

export const platformErrors = [ValidationFailed, Unauthenticated, Forbidden, IdempotencyConflict, NeedsInternet, StorageFull, RateLimited, InternalError] as const;
export type PlatformError = ErrorOf<typeof platformErrors>;

export function successEnvelope<T extends z.ZodType>(data: T) {
  return z.object({ success: z.literal(true), data, message: z.string() });
}

export function errorSchema(def: ErrorDef) {
  return z.object({ code: z.literal(def.code), retryable: z.boolean(), details: def.details });
}

/** The error envelope for a list of errors (platform errors included): `error` is a union discriminated by `code`. */
export function errorEnvelope(errors: readonly ErrorDef[]) {
  const [first, ...rest] = [...errors, ...platformErrors].map(errorSchema);
  if (first === undefined) throw new Error('unreachable: platform errors are never empty');
  return z.object({ success: z.literal(false), data: z.null(), message: z.string(), error: z.discriminatedUnion('code', [first, ...rest]) });
}
